/**
 * Deploy Store — E.21.36 Workstream (b) Frontend Deploy Wiring
 *
 * Mirrors buildStore's polling-based pattern for the Deploy lifecycle:
 *   - deployProject(projectId, slug?)  POST /api/deploys/{id}
 *   - pollDeployStatus(projectId)      GET  /api/deploys/{id}/status @ 1Hz
 *
 * Backend status values (verbatim from routes/deploys.py):
 *   'none' | 'syncing' | 'applying-nginx' | 'http-live' | 'error'    (Phase A)
 *   'https-live'                                                     (Phase B)
 *
 * Frontend collapses backend's 'error' → 'failed' (single terminal-error
 * label across the SPA).
 *
 * Reviewer-G0 CONCERNS-92 resolutions inline:
 *   C2  — MAX_POLLS exhaustion ALWAYS surfaces as 'failed', not infinite
 *         spinner. Mirror buildStore lines 446-458.
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { api, ApiError } from '@/services/api';
import type { DeploymentStatus, DeployListItem } from '@/types';

interface DeployResponse {
  projectId: string;
  slug: string;
  url: string;
  status: string;
  isNew: boolean;
}

interface DeployStatusResponse {
  projectId: string;
  status: string;
  slug?: string | null;
  url?: string | null;
  lastError?: string | null;
}

interface UndeployResponse {
  projectId: string;
  slug: string;
  status: string;
}

interface DeployListResponse {
  deploys: DeployListItem[];
}

// ── Gap A6 (2026-08-18): per-deploy coherence report ─────────────────────────
// GET /api/deploys/{id}/coherence — DB row ⇔ vibe vhost ⇔ docroot ⇔ cert SANs.
// The healfrommold split-brain class, surfaced in the Deploys view.
export interface CoherenceCheck {
  key: string;
  level: 'check' | 'info';
  ok: boolean;
  detail: string;
}

export interface CoherenceHost {
  apex: string;
  fqdn: string;
  certName: string;
  isCanonical: boolean;
  nginxHttpsAt: string | null;
  sanCovered: boolean | null;
  certState: string;
  lastError: string | null;
}

export interface CoherenceReport {
  projectId: string;
  slug: string;
  status: string;
  url: string;
  renderedAt: number | null;
  hosts: CoherenceHost[];
  checks: CoherenceCheck[];
  coherent: boolean;
}

export type CoherenceEntry =
  | { state: 'loading' }
  | { state: 'ready'; report: CoherenceReport }
  | { state: 'error'; message: string };

// 2026-06-27 — agent deploy confirm-gate. Deploy publishes to the public
// internet with NO auth, so an agent (Pi Chat / Voice) must NOT be able to
// publish on its own. The agent STAGES a deploy via requestDeploy(); the
// actual public publish only happens when a human clicks Confirm in the SPA
// banner, which calls confirmPendingDeploy(). This makes the gate TECHNICAL
// (the deploy tool literally cannot reach deployProject) rather than a
// prompt-text suggestion. See docs/20260627/..._AGENT_FULL_IDE_CONTROL_*.md §-1.
/**
 * ⭐⭐ THE BANNER HAS A LIFECYCLE, AND IT USED TO HAVE EXACTLY ONE FRAME.
 *
 * Operator, 2026-08-23, after clicking Confirm: *"notice that the agent did not
 * 'respond' to my confirm"* / *"as a user i dont know what i should do"*. The
 * deploy HAD succeeded. Nothing said so.
 *
 * ⛔ THE CAUSE WAS THE DOUBLE-FIRE GUARD ITSELF. `confirmPendingDeploy` set
 * `pendingDeploy = null` FIRST so a second click could not re-fire — and TopBar
 * renders the banner only while `pendingDeploy` is non-null. So the one surface
 * the human had just been told to use UNMOUNTED on click, the publish ran for
 * ~15s, and its outcome landed in `deploymentStatus`, which that banner never
 * read. Guarding by REMOVAL also removed the only place an answer could appear.
 *
 * ⇒ The guard is now the PHASE, not the absence. The banner stays mounted from
 * `staged` through to `done`/`failed` and reports the URL, the timestamp or the
 * error where the click happened. `cancelPendingDeploy()` is what clears it —
 * on `staged` that means "do not publish", and on a terminal phase it is the
 * dismiss.
 *
 * ⚠ The GATE is untouched: an agent still calls `requestDeploy()` and still
 * cannot reach `deployProject()`. This changes what the human sees afterwards,
 * not who is allowed to publish.
 */
export type PendingDeployPhase = 'staged' | 'publishing' | 'done' | 'failed';

interface PendingDeploy {
  projectId: string;
  slug?: string;
  phase: PendingDeployPhase;
}

interface DeployState {
  deploymentStatus: DeploymentStatus;
  deploys: DeployListItem[];
  // Staged-but-not-yet-published deploy awaiting human confirmation (null = none).
  pendingDeploy: PendingDeploy | null;
  deployProject: (projectId: string, slug?: string) => Promise<void>;
  pollDeployStatus: (projectId: string) => Promise<void>;
  resetDeploymentStatus: () => void;
  // Agent-facing: stage a deploy for human confirmation. Does NOT publish.
  requestDeploy: (projectId: string, slug?: string) => void;
  // Human-facing: confirm the staged deploy → publish (deployProject + poll).
  confirmPendingDeploy: () => Promise<void>;
  // Human-facing: discard the staged deploy without publishing.
  cancelPendingDeploy: () => void;
  // Take a deployed project DOWN (DELETE /api/deploys/{id}). Synchronous
  // on the backend (VPS cleanup inline), so no polling — just transition
  // 'removing' → 'not_deployed' on success.
  undeployProject: (projectId: string) => Promise<void>;
  // Admin Deploys view: GET /api/deploys → all this tenant's deploys.
  fetchDeploys: () => Promise<void>;
  // Per-deploy coherence report, keyed by projectId (gap A6).
  coherence: Record<string, CoherenceEntry>;
  fetchCoherence: (projectId: string) => Promise<void>;
  // Gap A3: swap the .prev docroot back into place. Calling twice returns to
  // where you started (the backend keeps the replaced root as the new .prev).
  rollbackProject: (projectId: string) => Promise<{ ok: boolean; error?: string }>;
}

const DEFAULT_DEPLOYMENT_STATUS: DeploymentStatus = {
  status: 'not_deployed',
  url: null,
  slug: null,
  lastError: null,
  deployedAt: null,
};

const POLL_INTERVAL_MS = 1000;
const MAX_POLLS = 600; // 10 minute hard cap (mirror buildStore)


export const useDeployStore = create<DeployState>()(
  immer((set, get) => ({
    deploymentStatus: DEFAULT_DEPLOYMENT_STATUS,
    deploys: [],
    pendingDeploy: null,

    resetDeploymentStatus: () =>
      set((state) => {
        state.deploymentStatus = DEFAULT_DEPLOYMENT_STATUS;
      }),

    requestDeploy: (projectId, slug) =>
      set((state) => {
        // Stage only — no backend call. The confirm banner reads this and the
        // human's click is the only thing that calls confirmPendingDeploy().
        state.pendingDeploy =
          slug !== undefined
            ? { projectId, slug, phase: 'staged' }
            : { projectId, phase: 'staged' };
      }),

    // Clears the banner. On `staged` this is "do not publish"; on `done` or
    // `failed` it is the dismiss. ⚠ It deliberately does NOT undo a publish —
    // taking a live deploy down is `undeployProject`, a different act.
    cancelPendingDeploy: () =>
      set((state) => {
        state.pendingDeploy = null;
      }),

    confirmPendingDeploy: async () => {
      const pending = get().pendingDeploy;
      // ⛔ THE DOUBLE-FIRE GUARD, and it is a PHASE CHECK rather than a clear.
      // Only a `staged` banner can publish, so a second click while publishing
      // (or after) does nothing — without destroying the banner, which is what
      // used to leave the human staring at silence.
      if (!pending || pending.phase !== 'staged') return;
      set((state) => {
        if (state.pendingDeploy) state.pendingDeploy.phase = 'publishing';
      });

      await get().deployProject(pending.projectId, pending.slug);
      // Only poll while the deploy is still in-flight. deployProject sets the
      // status from the POST response; if it already landed terminal
      // ('http-live'/'https-live'/'failed') or the backend returned a terminal
      // value, polling is unnecessary (and would hang a fake-timer test).
      const status = get().deploymentStatus.status;
      if (status === 'syncing' || status === 'applying-nginx') {
        await get().pollDeployStatus(pending.projectId);
      }

      // ⭐ REPORT THE OUTCOME WHERE THE CLICK HAPPENED. Everything needed is
      // already in `deploymentStatus` — url, deployedAt, lastError — so this
      // reads it rather than inventing a second source of truth.
      // ⚠ `failed` is derived from the STATUS, not from the absence of a url:
      // a publish that failed after a previous success still has the OLD url
      // sitting in state, and treating that as success would report a deploy
      // that did not happen.
      const final = get().deploymentStatus;
      set((state) => {
        // ⚠ Only if the banner is still ours. A cancel (or a fresh stage) while
        // the publish was in flight must not be overwritten by this result.
        if (!state.pendingDeploy || state.pendingDeploy.phase !== 'publishing') return;
        state.pendingDeploy.phase = final.status === 'failed' ? 'failed' : 'done';
      });
    },

    undeployProject: async (projectId) => {
      // Optimistic: surface 'removing' immediately (DELETE does VPS cleanup
      // inline, ~2-5s). On success the project is no longer deployed.
      set((state) => {
        state.deploymentStatus.status = 'removing';
        state.deploymentStatus.lastError = null;
      });
      try {
        await api.delete<UndeployResponse>(`/deploys/${projectId}`);
        set((state) => {
          state.deploymentStatus = DEFAULT_DEPLOYMENT_STATUS;
        });
      } catch (err) {
        // 404 = already undeployed (e.g. removed in another tab) → treat as
        // success: the project is down, which is the desired end-state.
        if (err instanceof ApiError && err.status === 404) {
          set((state) => {
            state.deploymentStatus = DEFAULT_DEPLOYMENT_STATUS;
          });
          return;
        }
        const msg = err instanceof ApiError
          ? `${err.status}: ${err.message}`
          : String(err);
        set((state) => {
          state.deploymentStatus.status = 'failed';
          state.deploymentStatus.lastError = msg;
        });
      }
    },

    coherence: {},

    rollbackProject: async (projectId) => {
      try {
        await api.post(`/deploys/${projectId}/rollback`, {});
        return { ok: true };
      } catch (err) {
        // The 409 body's `detail` is the useful part ("no snapshot exists…");
        // ApiError.message alone is just "API Error: 409 Conflict".
        let error: string;
        if (err instanceof ApiError) {
          const detail = (err.data as { detail?: unknown } | undefined)?.detail;
          error = typeof detail === 'string' ? detail : err.message;
        } else {
          error = String(err);
        }
        return { ok: false, error };
      }
    },

    fetchCoherence: async (projectId) => {
      set((state) => {
        state.coherence[projectId] = { state: 'loading' };
      });
      try {
        const report = await api.get<CoherenceReport>(
          `/deploys/${projectId}/coherence`,
        );
        set((state) => {
          state.coherence[projectId] = { state: 'ready', report };
        });
      } catch (err) {
        const message =
          err instanceof ApiError ? err.message : String(err);
        set((state) => {
          state.coherence[projectId] = { state: 'error', message };
        });
      }
    },

    fetchDeploys: async () => {
      try {
        const resp = await api.get<DeployListResponse>('/deploys');
        set((state) => {
          state.deploys = resp.deploys;
        });
      } catch (err) {
        // Non-fatal: leave the previous list in place (the admin view shows
        // a stale-but-harmless list rather than blanking on a transient error).
        // Surface to the console so a persistently-broken list (401/500) is
        // diagnosable rather than silently swallowed.
        console.warn('[deployStore] fetchDeploys failed:', err);
      }
    },

    deployProject: async (projectId, slug) => {
      // Optimistic: backend allocates row + kicks bg task synchronously,
      // returns 200 with status='syncing'. Even before the response lands
      // we surface the in-flight state to the UI so the button can
      // swap-in-place to "Deploying..."
      set((state) => {
        state.deploymentStatus.status = 'syncing';
        state.deploymentStatus.lastError = null;
      });

      const body: { slug?: string } = {};
      if (slug !== undefined) body.slug = slug;

      try {
        const resp = await api.post<DeployResponse>(`/deploys/${projectId}`, body);
        set((state) => {
          state.deploymentStatus.status = (resp.status || 'syncing') as DeploymentStatus['status'];
          state.deploymentStatus.slug = resp.slug;
          state.deploymentStatus.url = resp.url;
          state.deploymentStatus.lastError = null;
        });
      } catch (err) {
        const msg = err instanceof ApiError
          ? `${err.status}: ${err.message}`
          : String(err);
        set((state) => {
          state.deploymentStatus.status = 'failed';
          state.deploymentStatus.lastError = msg;
        });
      }
    },

    pollDeployStatus: async (projectId) => {
      // Mirror buildStore.pollBuildStatus pattern. Poll @ 1Hz until terminal
      // ('http-live' | 'https-live' | 'error' | 'none' after first tick).
      // After MAX_POLLS exhausts, surface as 'failed' (NOT infinite spinner).
      // Reviewer C2: transient HTTP errors are swallowed mid-poll (deploy
      // operations involve VPS network roundtrips; transient failures are
      // expected). MAX_POLLS exhaustion is the surfacing safety net.
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        let resp: DeployStatusResponse;
        try {
          resp = await api.get<DeployStatusResponse>(
            `/deploys/${projectId}/status`,
          );
        } catch {
          // Transient network error — keep polling. MAX_POLLS exhaustion
          // catches the genuinely-down case below.
          continue;
        }

        // Terminal: success
        //
        // ⚠ 'content-live' is a content-only publish (rsync'd the docroot and
        // stopped — no vhost, no certificate). It is a SUCCESS and must be
        // listed here explicitly: the fall-through at the bottom of this loop
        // treats anything unrecognised as still-in-flight, so omitting it made
        // a completed publish poll for ten minutes and then report a timeout.
        if (
          resp.status === 'http-live' ||
          resp.status === 'https-live' ||
          resp.status === 'content-live'
        ) {
          set((state) => {
            state.deploymentStatus.status =
              resp.status as DeploymentStatus['status'];
            if (resp.slug) state.deploymentStatus.slug = resp.slug;
            if (resp.url) state.deploymentStatus.url = resp.url;
            state.deploymentStatus.deployedAt = new Date().toISOString();
            state.deploymentStatus.lastError = null;
          });
          return;
        }

        // Terminal: error
        if (resp.status === 'error') {
          set((state) => {
            state.deploymentStatus.status = 'failed';
            state.deploymentStatus.lastError =
              resp.lastError ?? 'deploy failed';
          });
          return;
        }

        // Terminal: backend lost the deploy record (service restart)
        if (resp.status === 'none') {
          set((state) => {
            state.deploymentStatus.status = 'failed';
            state.deploymentStatus.lastError =
              'deploy status lost on backend (service restart?)';
          });
          return;
        }

        // In-flight: 'syncing' | 'applying-nginx' — update + continue
        set((state) => {
          state.deploymentStatus.status =
            resp.status as DeploymentStatus['status'];
          if (resp.slug) state.deploymentStatus.slug = resp.slug;
          if (resp.url) state.deploymentStatus.url = resp.url;
        });
      }

      // Reviewer C2: hard cap reached — surface as failure rather than
      // leaving the user staring at an infinite spinner.
      set((state) => {
        state.deploymentStatus.status = 'failed';
        state.deploymentStatus.lastError =
          'deploy timed out after 10 minutes (MAX_POLLS=600 exhausted)';
      });
    },
  })),
);
